import type { Directive } from 'vue'
import type { NavigateFocusBinding, Direction, TriggerKey, ElementPosition } from './types'
import { navigateFocus } from './keyboardShortcuts'

const getFocusableElements = (): HTMLElement[] => {
  const selector = 'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
  const elements = Array.from(document.querySelectorAll(selector)) as HTMLElement[]

  return elements
    .filter(el => {
      const isDisabled = (el as any).disabled
      return !isDisabled && el.offsetParent !== null
    })
    .sort((a, b) => {
      const aIndex = parseInt(a.getAttribute('tabindex') || '0')
      const bIndex = parseInt(b.getAttribute('tabindex') || '0')

      if (aIndex > 0 && bIndex > 0) return aIndex - bIndex
      if (aIndex > 0) return -1
      if (bIndex > 0) return 1
      return 0
    })
}

const getElementPositions = (): ElementPosition[] => {
  const elements = getFocusableElements()
  return elements.map(element => ({
    element,
    rect: element.getBoundingClientRect()
  }))
}

const findClosestElement = (currentElement: HTMLElement, direction: Direction): HTMLElement | null => {
  const positions = getElementPositions()
  const currentPos = positions.find(pos => pos.element === currentElement)

  if (!currentPos) return null

  const currentRect = currentPos.rect
  let bestMatch: HTMLElement | null = null
  let bestDistance = Infinity

  positions.forEach(pos => {
    if (pos.element === currentElement) return

    const rect = pos.rect
    let isInDirection = false
    let distance = 0

    switch (direction) {
      case 'up':
        isInDirection = rect.bottom < currentRect.top
        if (isInDirection) {
          const horizontalOverlap = !(rect.right < currentRect.left || rect.left > currentRect.right)
          distance = currentRect.top - rect.bottom + (horizontalOverlap ? 0 : 1000)
        }
        break
      case 'down':
        isInDirection = rect.top > currentRect.bottom
        if (isInDirection) {
          const horizontalOverlap = !(rect.right < currentRect.left || rect.left > currentRect.right)
          distance = rect.top - currentRect.bottom + (horizontalOverlap ? 0 : 1000)
        }
        break
      case 'left':
        isInDirection = rect.right < currentRect.left
        if (isInDirection) {
          const verticalOverlap = !(rect.bottom < currentRect.top || rect.top > currentRect.bottom)
          distance = currentRect.left - rect.right + (verticalOverlap ? 0 : 1000)
        }
        break
      case 'right':
        isInDirection = rect.left > currentRect.right
        if (isInDirection) {
          const verticalOverlap = !(rect.bottom < currentRect.top || rect.top > currentRect.bottom)
          distance = rect.left - currentRect.right + (verticalOverlap ? 0 : 1000)
        }
        break
      case 'next':
      case 'prev':
        return
    }

    if (isInDirection && distance < bestDistance) {
      bestMatch = pos.element
      bestDistance = distance
    }
  })

  return bestMatch
}

const navigateSequential = (currentElement: HTMLElement, direction: 'next' | 'prev'): void => {
  const elements = getFocusableElements()
  const currentIndex = elements.indexOf(currentElement)

  if (currentIndex === -1) return

  let nextIndex
  if (direction === 'next') {
    nextIndex = (currentIndex + 1) % elements.length
  } else {
    nextIndex = (currentIndex - 1 + elements.length) % elements.length
  }

  elements[nextIndex].focus()
}

const keyToDirection: Record<string, Direction> = {
  'ArrowUp': 'up',
  'ArrowDown': 'down',
  'ArrowLeft': 'left',
  'ArrowRight': 'right',
  'Tab': 'next',
  'Enter': 'next'
}

export const navigateFocusDirective: Directive<HTMLElement, string> = {
  mounted(el: HTMLElement, binding: NavigateFocusBinding) {
    const navigateToTarget = (direction?: Direction) => {
      if (binding.value) {
        navigateFocus(binding.value)
        return
      }

      if (direction) {
        if (direction === 'next' || direction === 'prev') {
          navigateSequential(el, direction)
        } else {
          const nextElement = findClosestElement(el, direction)
          if (nextElement) {
            nextElement.focus()
          }
        }
        return
      }

      navigateSequential(el, 'next')
    }

    const _handleKeydown = (e: KeyboardEvent) => {
      const triggerKeys: TriggerKey[] = binding.arg
        ? binding.arg.split(',').map(k => k.trim()) as TriggerKey[]
        : ['Enter']

      if (!triggerKeys.includes(e.key as TriggerKey)) {
        return
      }

      const direction = keyToDirection[e.key]

      if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault()
        navigateToTarget('prev')
        return
      }

      e.preventDefault()
      navigateToTarget(direction)
    }

    const handleCustomEvent = (e: CustomEvent) => {
      const direction = e.detail?.direction as Direction | undefined
      navigateToTarget(direction)
    }

    el.addEventListener('keydown', _handleKeydown)
    el.addEventListener('navigate-focus', handleCustomEvent as EventListener)

    ;(el as any)._navigateFocusHandlers = {
      keydown: _handleKeydown,
      custom: handleCustomEvent
    }
  },

  unmounted(el: HTMLElement) {
    const handlers = (el as any)._navigateFocusHandlers
    if (handlers) {
      el.removeEventListener('keydown', handlers.keydown)
      el.removeEventListener('navigate-focus', handlers.custom)
      delete (el as any)._navigateFocusHandlers
    }
  }
}